import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import api, { formatApiErrorDetail } from "../api/client";
import { Rocket, Building2, Share2, Gem, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const TABS = [
    { k: "launchpad", label: "Launchpad", Icon: Rocket },
    { k: "institutional", label: "Institutional Desk", Icon: Building2 },
    { k: "portability", label: "Rights Portability", Icon: Share2 },
    { k: "nft", label: "NFT Certificate", Icon: Gem },
    { k: "academic", label: "Academic Licence", Icon: GraduationCap },
];

export default function DistributionHub() {
    const [tab, setTab] = useState("launchpad");
    const [films, setFilms] = useState([]);
    const [deals, setDeals] = useState([]);
    const [filmId, setFilmId] = useState("");
    const [dealId, setDealId] = useState("");
    const [lpForm, setLpForm] = useState({ pitch: "" });
    const [lpShowcase, setLpShowcase] = useState([]);
    const [inst, setInst] = useState(null);
    const [portForm, setPortForm] = useState({ platform: "vimeo_ott", territory: "Worldwide" });
    const [portJobs, setPortJobs] = useState([]);
    const [nfts, setNfts] = useState([]);
    const [acaForm, setAcaForm] = useState({ institution_name: "", purpose: "" });

    useEffect(() => {
        (async () => {
            const f = await api.get("/films"); setFilms(f.data);
            if (f.data[0]) setFilmId(f.data[0].id);
            try { const d = await api.get("/deals"); setDeals(d.data.filter(x => x.status === "transfer_complete")); if (d.data[0]) setDealId(d.data[0].id); } catch (_) {}
            try { const s = await api.get("/launchpad/showcase"); setLpShowcase(s.data); } catch (_) {}
            try { const n = await api.get("/nft/mine"); setNfts(n.data); } catch (_) {}
            try { const i = await api.get("/institutional/dashboard"); setInst(i.data); } catch (_) {}
        })();
    }, []);

    const applyLaunchpad = async (e) => {
        e.preventDefault();
        try { await api.post("/launchpad/apply", { film_id: filmId, pitch: lpForm.pitch }); toast.success("Application under review"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };
    const pushPortability = async () => {
        if (!dealId) return toast.error("Select a completed deal first");
        try { const { data } = await api.post("/portability/push", { deal_id: dealId, ...portForm }); setPortJobs([data, ...portJobs]); toast.success("Distribution push queued (MOCKED)"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };
    const mintNFT = async () => {
        if (!dealId) return toast.error("Select a completed deal first");
        try { const { data } = await api.post(`/nft/mint/${dealId}`); setNfts([data, ...nfts.filter(n => n.deal_id !== dealId)]); toast.success("Soulbound certificate minted (MOCKED)"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };
    const applyAcademic = async (e) => {
        e.preventDefault();
        try { await api.post("/academic/apply", { film_id: filmId, ...acaForm }); toast.success("Pre-approved for 70% academic discount"); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    };

    return (
        <div className="min-h-screen bg-ink-300 text-silver-100">
            <Navbar />
            <section className="max-w-[1400px] mx-auto px-4 md:px-10 pt-12 pb-24">
                <div className="text-[11px] tracking-[0.3em] uppercase text-platinum mb-3 flex items-center gap-2">
                    <Rocket className="w-4 h-4" /> Platform & Network
                </div>
                <h1 className="font-display text-5xl md:text-6xl leading-none mb-3">Moats that <span className="silver-text italic">compound.</span></h1>
                <p className="text-silver-400 mb-10 max-w-2xl">Launchpad residency, institutional white-label desk, cross-platform rights portability, NFT certificates, and academic licensing.</p>

                <div className="flex items-center gap-6 border-b border-white/5 mb-8 overflow-x-auto">
                    {TABS.map(({ k, label, Icon }) => (
                        <button key={k} onClick={() => setTab(k)} className={`flex items-center gap-2 py-4 border-b-2 whitespace-nowrap text-xs uppercase tracking-widest ${tab === k ? "border-platinum text-platinum" : "border-transparent text-silver-500 hover:text-white"}`} data-testid={`dist-tab-${k}`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </div>

                {tab === "launchpad" && (
                    <div data-testid="launchpad-panel" className="grid md:grid-cols-2 gap-8">
                        <form onSubmit={applyLaunchpad} className="border border-white/10 rounded-sm p-6 bg-ink-200/40">
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-4">Launchpad residency application</div>
                            <div className="text-silver-300 text-sm mb-4">Top 20 creators per year: <span className="text-platinum">$25k grant + listing priority + 0% platform fee on first sale.</span></div>
                            <select value={filmId} onChange={(e) => setFilmId(e.target.value)} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-3">
                                {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                            </select>
                            <textarea rows={5} placeholder="Pitch (why this film, why now)" value={lpForm.pitch} onChange={(e) => setLpForm({ pitch: e.target.value })} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" required />
                            <button className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="launchpad-apply-btn">Apply</button>
                        </form>
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Current cohort</div>
                            {lpShowcase.length === 0 ? <div className="text-silver-500 text-sm">Inaugural cohort opens soon.</div> : (
                                <div className="space-y-2">{lpShowcase.map((a) => <div key={a.id} className="p-3 border border-white/10 rounded-sm text-silver-200 text-sm">{a.applicant_name}</div>)}</div>
                            )}
                        </div>
                    </div>
                )}

                {tab === "institutional" && inst && (
                    <div data-testid="institutional-panel">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            <Stat label="Inventory" value={inst.inventory} />
                            <Stat label="Live auctions" value={inst.live_auctions} />
                            <Stat label="Deals YTD" value={inst.closed_deals_ytd} />
                            <Stat label="Value YTD" value={`$${(inst.closed_value_usd_ytd / 1000000).toFixed(2)}M`} />
                        </div>
                        <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Top acquisition targets</div>
                        <div className="grid md:grid-cols-2 gap-3">
                            {(inst.top_picks || []).map((p) => (
                                <div key={p.id} className="flex items-center justify-between p-4 border border-white/10 rounded-sm bg-ink-200/40">
                                    <div>
                                        <div className="text-silver-200 text-sm">{p.title}</div>
                                        <div className="text-[11px] text-silver-500">{p.country} · {p.language} · {p.views || 0} views</div>
                                    </div>
                                    <div className="text-platinum font-mono tabular-nums">${Number(p.price_usd || 0).toLocaleString()}</div>
                                </div>
                            ))}
                        </div>
                        <div className="text-[11px] text-silver-500 mt-6">MOCKED white-label — $50k/mo per institutional seat</div>
                    </div>
                )}

                {tab === "portability" && (
                    <div data-testid="portability-panel" className="max-w-3xl">
                        <div className="text-silver-400 text-sm mb-5">After a completed purchase, push the film to any major platform with one click.</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                            <select value={dealId} onChange={(e) => setDealId(e.target.value)} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm">
                                <option value="">Select completed deal…</option>
                                {deals.map((d) => <option key={d.id} value={d.id}>{d.film_title}</option>)}
                            </select>
                            <select value={portForm.platform} onChange={(e) => setPortForm({ ...portForm, platform: e.target.value })} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm">
                                <option value="vimeo_ott">Vimeo OTT</option>
                                <option value="roku">Roku Channel</option>
                                <option value="plex">Plex</option>
                                <option value="amazon_videodirect">Amazon Video Direct</option>
                            </select>
                            <input value={portForm.territory} onChange={(e) => setPortForm({ ...portForm, territory: e.target.value })} placeholder="Territory" className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm" />
                        </div>
                        <button onClick={pushPortability} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="portability-push-btn">Push distribution</button>
                        {portJobs.length > 0 && (
                            <div className="mt-6 space-y-2">
                                {portJobs.map((j) => (
                                    <div key={j.id} className="p-3 border border-white/10 rounded-sm text-sm text-silver-300">
                                        {j.platform} · {j.territory} · ETA {j.eta_hours}h · <span className="text-[10px] uppercase tracking-widest text-platinum">{j.status}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === "nft" && (
                    <div data-testid="nft-panel" className="max-w-3xl">
                        <div className="text-silver-400 text-sm mb-5">Optionally mint your ownership credential as a soulbound NFT for Web3 provenance. Off by default.</div>
                        <div className="flex gap-3 mb-6 flex-wrap">
                            <select value={dealId} onChange={(e) => setDealId(e.target.value)} className="bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm">
                                <option value="">Select completed deal…</option>
                                {deals.map((d) => <option key={d.id} value={d.id}>{d.film_title}</option>)}
                            </select>
                            <button onClick={mintNFT} className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="nft-mint-btn">Mint soulbound certificate</button>
                        </div>
                        {nfts.length > 0 && (
                            <div className="space-y-3">
                                <div className="text-[10px] tracking-widest uppercase text-silver-500">Your certificates</div>
                                {nfts.map((n) => (
                                    <div key={n.id} className="border border-white/10 rounded-sm p-4 bg-ink-200/40">
                                        <div className="text-[10px] tracking-widest uppercase text-platinum mb-1">{n.network}</div>
                                        <div className="font-mono text-xs text-silver-300 break-all">Token: {n.token_id}</div>
                                        <div className="font-mono text-[10px] text-silver-500 break-all mt-1">Chain hash: {n.chain_hash}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="text-[11px] text-silver-500 mt-4">MOCKED mint — integrate Polygon / Ethereum via ethers.js for real</div>
                    </div>
                )}

                {tab === "academic" && (
                    <div data-testid="academic-panel" className="max-w-3xl">
                        <form onSubmit={applyAcademic} className="border border-white/10 rounded-sm p-6 bg-ink-200/40">
                            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-3">Academic / educational licence</div>
                            <div className="text-silver-300 text-sm mb-5">Universities, film schools, NGOs get an auto <span className="text-platinum">70% discount</span> with attribution watermarks.</div>
                            <select value={filmId} onChange={(e) => setFilmId(e.target.value)} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-3">
                                {films.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
                            </select>
                            <input placeholder="Institution name" value={acaForm.institution_name} onChange={(e) => setAcaForm({ ...acaForm, institution_name: e.target.value })} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-3" required />
                            <textarea rows={4} placeholder="Purpose (course, screening, research)" value={acaForm.purpose} onChange={(e) => setAcaForm({ ...acaForm, purpose: e.target.value })} className="w-full bg-ink-300 border border-white/10 px-3 py-2 text-sm rounded-sm mb-4" required />
                            <button className="bg-platinum hover:bg-white text-ink-400 px-5 py-2.5 text-xs tracking-widest uppercase rounded-sm" data-testid="academic-apply-btn">Apply</button>
                        </form>
                    </div>
                )}
            </section>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-white/10 rounded-sm p-5 bg-ink-200/40">
            <div className="text-[10px] tracking-widest uppercase text-silver-500 mb-2">{label}</div>
            <div className="font-display text-3xl silver-text tabular-nums">{value}</div>
        </div>
    );
}
